// Shared constants for the document checker POD/OCR screens.

export const AI_DETAILS_FILTER_KEY = 'AI_DETAILS';

/**
 * Kill switch for the whole POD OCR feature. The AI filters and bulk approve are
 * shown only when it is on: with it off there are no extractions to filter or
 * approve, so the filter returns nothing and bulk approve finds nothing — which
 * reads as a broken screen rather than an absent feature.
 */
export const AI_FEATURE_ENABLED_PROPERTY = 'POD_AI_FEATURE_ENABLED';

/**
 * Max number of GET /pod/extraction/{cnote} calls in flight while assembling the
 * bulk-approve set, so a large result page doesn't fire one request per row all
 * at once.
 */
export const EXTRACTION_FETCH_CONCURRENCY = 6;

// Session property seeded in zoom_properties (V7.16.10__pod_ocr_t37665.sql).
export const HIGH_CONFIDENCE_THRESHOLD_PROPERTY =
  'POD_HIGH_CONFIDENCE_THRESHOLD';
export const DEFAULT_HIGH_CONFIDENCE_THRESHOLD = 95;
export const MEDIUM_CONFIDENCE_THRESHOLD = 80;

export const APPROVE_POD_ROLE = 'ROLE_DOCUMENT_CHECKER_APPROVE_POD';
export const ADD_REMARKS_POD_ROLE = 'ROLE_DOCUMENT_CHECKER_ADD_REMARKS_POD';

/**
 * Mirrors the backend ReviewStatus enum. Note this is distinct from
 * ValidationOutcome (PENDING, ACCEPTED, REJECTED_*, SKIPPED, SHADOW) — approval
 * moves a POD along ReviewStatus and never rewrites its ValidationOutcome.
 */
export const REVIEW_STATUS = {
  NOT_APPLICABLE: 'NOT_APPLICABLE',
  PENDING_REVIEW: 'PENDING_REVIEW',
  REVIEWED: 'REVIEWED'
};

/** Mirrors the backend ValidationOutcome enum. Distinct from REVIEW_STATUS. */
export const VALIDATION_OUTCOME = {
  PENDING: 'PENDING',
  ACCEPTED: 'ACCEPTED',
  REJECTED_CNOTE_MISMATCH: 'REJECTED_CNOTE_MISMATCH',
  REJECTED_LOW_CONFIDENCE: 'REJECTED_LOW_CONFIDENCE',
  REJECTED_UNREADABLE: 'REJECTED_UNREADABLE',
  REJECTED_INCORRECT_POD: 'REJECTED_INCORRECT_POD',
  SKIPPED: 'SKIPPED',
  SHADOW: 'SHADOW'
};

/**
 * The AI Status badge reports what the extraction concluded, so it is keyed on
 * ValidationOutcome and read from PodExtractionResponseDTO — not from the list
 * row, which carries neither. ReviewStatus answers a different question (has a
 * human signed this off) and drives eligibility, not this badge.
 */
export const VALIDATION_OUTCOME_BADGE_CLASS = {
  [VALIDATION_OUTCOME.ACCEPTED]: 'badgeGreen',
  [VALIDATION_OUTCOME.PENDING]: 'badgeYellow',
  [VALIDATION_OUTCOME.SHADOW]: 'badgeYellow',
  [VALIDATION_OUTCOME.REJECTED_CNOTE_MISMATCH]: 'badgeRed',
  [VALIDATION_OUTCOME.REJECTED_LOW_CONFIDENCE]: 'badgeRed',
  [VALIDATION_OUTCOME.REJECTED_UNREADABLE]: 'badgeRed',
  [VALIDATION_OUTCOME.REJECTED_INCORRECT_POD]: 'badgeRed',
  [VALIDATION_OUTCOME.SKIPPED]: 'badgeGrey'
};

export const REVIEW_STATUS_BADGE_CLASS = {
  [REVIEW_STATUS.NOT_APPLICABLE]: 'badgeGrey',
  [REVIEW_STATUS.PENDING_REVIEW]: 'badgeYellow',
  [REVIEW_STATUS.REVIEWED]: 'badgeGreen'
};

export const AI_STATUS_FILTER_ALL = 'ALL';

export const AI_STATUS_OPTIONS = [
  { key: 'ALL', value: AI_STATUS_FILTER_ALL },
  { key: 'PENDING REVIEW', value: REVIEW_STATUS.PENDING_REVIEW },
  { key: 'REVIEWED', value: REVIEW_STATUS.REVIEWED },
  { key: 'NOT APPLICABLE', value: REVIEW_STATUS.NOT_APPLICABLE }
];

const STAMP_OPTIONS = [
  'NOT_PRESENT',
  'NOT_VISIBLE',
  'HALF_STAMPED',
  'PRESENT_AND_CORRECT'
];

const SIGNATURE_OPTIONS = ['NOT_PRESENT', 'NOT_VISIBLE', 'PRESENT_AND_CORRECT'];

const IMAGE_QUALITY_OPTIONS = [
  'INCORRECT_CNOTE_ON_POD',
  'INCORRECT_DOCUMENT',
  'BLURRED_IMAGE',
  'HALF_IMAGE',
  'CORRECT_AND_GOOD_QUALITY'
];

/**
 * Single source of truth linking a section field's display label (supplied by
 * the backend) to the PodExtractionResponseDTO field it is prefilled from and
 * to its allowed enum values. A label change on the backend now only has to be
 * reflected here.
 *
 * `submitField` is only needed where the approve/remarks payload names a field
 * differently from the extraction response — currently just image quality.
 */
// Deliberately excluded: 'Total Boxes' (consignment data, rendered view-only — the AI must never
// supply it) and 'Others' (a MiscellaneousPodIssues enum on submit, while the extraction returns free
// text, and it renders no control to correct).
export const POD_EXTRACTION_FIELDS = [
  { label: 'Received Boxes', field: 'receivedBoxes' },
  { label: 'Damaged Boxes', field: 'damagedBoxes' },
  { label: 'Missing Boxes', field: 'missingBoxes' },
  { label: 'Damaged Items', field: 'damagedItems' },
  { label: 'Missing Items', field: 'missingItems' },
  { label: 'Consignee Stamp', field: 'consigneeStamp', options: STAMP_OPTIONS },
  {
    label: 'Consignee Signature',
    field: 'consigneeSignature',
    options: SIGNATURE_OPTIONS
  },
  { label: 'Consignor Stamp', field: 'consignorStamp', options: STAMP_OPTIONS },
  {
    label: 'Consignor Signature',
    field: 'consignorSignature',
    options: SIGNATURE_OPTIONS
  },
  {
    label: 'POD Image Quality',
    field: 'imageQuality',
    submitField: 'podImageQuality',
    options: IMAGE_QUALITY_OPTIONS
  },
  { label: 'Remarks', field: 'remarks' }
];

export const EXTRACTION_FIELD_BY_LABEL = POD_EXTRACTION_FIELDS.reduce(
  (acc, { label, field }) => ({ ...acc, [label]: field }),
  {}
);

export const ENUM_OPTIONS = POD_EXTRACTION_FIELDS.reduce(
  (acc, { label, options }) => (options ? { ...acc, [label]: options } : acc),
  {}
);

/**
 * User-facing copy for the ZBPOD error codes the bulk-approve endpoint can
 * return. Kept as a single module-level source in sync with the backend
 * error_code_detail seeds (V7.16.10__pod_ocr_t37665.sql, ZBPOD0017-0023) so a
 * wording drift between the two shows up here and nowhere else.
 */
export const BULK_APPROVE_ERROR_MESSAGES = {
  ZBPOD0017:
    'The CN number on this POD does not match the expected CN. Please photograph the correct document.',
  ZBPOD0018:
    'POD confidence is below the required minimum. Please retake with better lighting and hold the phone steady.',
  ZBPOD0019:
    'The image could not be read clearly. Please retake with better lighting and hold the phone steady.',
  ZBPOD0020: 'No POD extraction result found for the CN.',
  ZBPOD0021: 'One or more selected PODs are not eligible for bulk approval.',
  ZBPOD0022: "You are not authorized for this POD's delivery OU.",
  ZBPOD0023: 'This POD was flagged as an incorrect / wrong document.'
};
